import { type AbPackageRaw, type PackageRaw } from '@marpla/api-sdk';
import { financeUtil } from '@marpla/core/util/finance.util';

type AbPackageType = 'paid' | 'free' | 'deposit';

interface Package {
  range: { min: number; max: number };
  price: number;
  isCurrent: boolean;
}

interface AbPackageCount {
  expiredAt: string;
  executionsLeft: number;
  type: AbPackageType | null;
  price: number;
}

interface AbPackageLocal {
  price: number;
  withTariff: boolean;
  lastDate: string | null;
  packages: Package[];
  packagesWithTariff: Package[];
  packagesWithoutTariff: Package[];
  packagesCounts: AbPackageCount[];
  priceAddAccount: number;
  priceForNotUnlimit?: number;
  wbAccounts: number;
  currentWbAccounts?: number;
  isUnlimit: boolean;
}

const mapApiPackageToLocal = (p: PackageRaw): Package => ({
  range: p.range,
  price: p.price,
  isCurrent: p.is_current,
});

const getPackageList = (packages: AbPackageRaw['packages'] | undefined): PackageRaw[] =>
  Array.isArray(packages) ? packages : [];

export const adaptAbPackage = (raw: AbPackageRaw): AbPackageLocal => {
  const core = financeUtil.adaptAbPackage(raw);
  const rawWithAccountFields = raw as AbPackageRaw & {
    price_for_not_unlimit?: number;
    current_wb_accounts?: number;
  };
  const coreWithAccountFields = core as typeof core & {
    priceForNotUnlimit?: number;
    currentWbAccounts?: number;
  };
  const priceForNotUnlimit =
    coreWithAccountFields.priceForNotUnlimit ?? rawWithAccountFields.price_for_not_unlimit;
  const currentWbAccounts =
    coreWithAccountFields.currentWbAccounts ?? rawWithAccountFields.current_wb_accounts;
  const currentWbAccountsNumber = Number(currentWbAccounts);

  const withTariffList = getPackageList(core.packagesWithTariff).map(mapApiPackageToLocal);
  const withoutTariffList = getPackageList(core.packagesWithoutTariff).map(mapApiPackageToLocal);

  const activeList = core.withTariff ? withTariffList : withoutTariffList;

  const packagesCounts: AbPackageCount[] = (core.packagesCounts ?? []).map((c) => ({
    expiredAt: c.expired_at ?? '',
    executionsLeft: c.executions_left,
    type: c.type ?? null,
    price: c.price ?? 0,
  }));

  return {
    price: core.price,
    withTariff: core.withTariff,
    lastDate: core.lastDate,
    packages: activeList,
    packagesWithTariff: withTariffList,
    packagesWithoutTariff: withoutTariffList,
    packagesCounts,
    priceAddAccount: core.priceAddAccount,
    priceForNotUnlimit: Number.isFinite(Number(priceForNotUnlimit))
      ? Number(priceForNotUnlimit)
      : undefined,
    wbAccounts: core.wbAccounts,
    currentWbAccounts:
      currentWbAccounts != null &&
      Number.isFinite(currentWbAccountsNumber) &&
      currentWbAccountsNumber > 0
        ? currentWbAccountsNumber
        : core.wbAccounts,
    isUnlimit: core.isUnlimit,
  };
};
